"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type AskResponse = {
  answer: string;
  answerMarkdown?: string;
  sources?: Array<{ title?: string; url?: string }>;
  provider: "ollama" | "tavily" | "duckduckgo" | "wikipedia" | "stackexchange";
  model?: string;
};

export default function AskAI() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<AskResponse | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textAreaRef = useRef<HTMLInputElement | null>(null);

  // Prefill from current selection for quick questions
  useEffect(() => {
    function handleSelectionChange() {
      const sel = window.getSelection?.();
      if (!sel) return;
      const value = (sel.toString?.() ?? "").trim();
      if (!value) return;
      // Only prefill if input is empty to avoid clobbering
      setQuestion((prev) => (prev ? prev : value));
    }
    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  const canAsk = question.trim().length > 0 && !loading;

  const submit = useCallback(async () => {
    const q = question.trim();
    if (!q) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const res = await fetch("/api/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }
      const data = (await res.json()) as AskResponse;
      setResponse(data);
    } catch (err) {
      if (
        err &&
        typeof err === "object" &&
        (err as { name?: string }).name === "AbortError"
      )
        return;
      setError("Unable to get an answer. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [question]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (canAsk) void submit();
      }
    },
    [canAsk, submit]
  );

  const copyAnswer = useCallback(async () => {
    const text = response?.answer ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  }, [response]);

  return (
    <div className=" flex h-full w-full flex-col gap-3 p-3">
      <div className="flex items-center gap-2">
        <label htmlFor="askai-input" className="sr-only">
          Ask a question
        </label>
        <input
          id="askai-input"
          ref={textAreaRef}
          type="text"
          className="min-w-[220px] flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          placeholder="Ask a short question and press Enter…"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          className="h-9 rounded-md bg-accent px-4 text-sm text-accent-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => canAsk && submit()}
          disabled={!canAsk}
          aria-disabled={!canAsk}
        >
          Ask
        </button>
      </div>

      <div className="relative flex-1 overflow-auto rounded-lg border border-border bg-background">
        {loading ? (
          <div className="p-4 text-sm">Thinking…</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-500">{error}</div>
        ) : !response ? (
          <div className="p-4 text-sm text-foreground/70">
            Type your question above and press Enter to get a quick answer.
          </div>
        ) : (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-foreground/60">
                Answer
              </div>
              <button
                className="h-8 rounded-md bg-surface px-3 text-xs hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                onClick={copyAnswer}
              >
                Copy
              </button>
            </div>
            <div className="text-xs text-foreground/60">
              Provider:{" "}
              <span className="rounded bg-surface px-2 py-0.5">
                {response.provider}
                {response.model ? ` · ${response.model}` : ""}
              </span>
            </div>
            <div className="prose prose-invert max-w-none text-sm leading-6">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: (props) => (
                    <a
                      {...props}
                      className="text-accent underline-offset-2 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                  code: (props) => {
                    const { children, ...rest } = props;
                    return (
                      <code
                        {...rest}
                        className="rounded bg-surface px-1 py-0.5 text-[0.85em]"
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: (props) => {
                    const { children, ...rest } = props;
                    return (
                      <pre
                        {...rest}
                        className="rounded-md bg-surface p-3 overflow-auto text-xs leading-5 my-2"
                      >
                        {children}
                      </pre>
                    );
                  },
                  p: (props) => <p {...props} className="my-2" />,
                  ul: (props) => (
                    <ul {...props} className="my-2 list-disc pl-5" />
                  ),
                  ol: (props) => (
                    <ol {...props} className="my-2 list-decimal pl-5" />
                  ),
                  h1: (props) => (
                    <h1 {...props} className="text-base font-semibold mt-2" />
                  ),
                  h2: (props) => (
                    <h2 {...props} className="text-sm font-semibold mt-2" />
                  ),
                }}
              >
                {response.answerMarkdown ?? response.answer}
              </ReactMarkdown>
            </div>
            {response.sources && response.sources.length > 0 ? (
              <div className="pt-2">
                <div className="mb-1 text-xs uppercase tracking-wide text-foreground/60">
                  Sources
                </div>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  {response.sources.slice(0, 5).map((s, i) => (
                    <li key={`src-${i}`}>
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent underline-offset-2 hover:underline"
                        >
                          {s.title || s.url}
                        </a>
                      ) : (
                        <span>{s.title}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
